import CustomTableToolbar, { TableFilterOptions } from "./CustomTableToolbar";
import TableContainer from "@mui/material/TableContainer";
import CustomTableHead from "./CustomTableHead";
import TableBody from "@mui/material/TableBody";
import CustomTableRow from "./CustomTableRow";
import { SortOrder, TABLE_ACTIONS } from "helpers/constants";
import TableEmptyRows from "./TableEmptyRows";
import {
  applyFilter,
  emptyRows,
  getComparator,
  getCustomComparator,
} from "./utils";
import TableNoData from "./TableNoData";
import TablePagination from "@mui/material/TablePagination";
import { memo, useEffect, useState } from "react";
import Table from "@mui/material/Table";
import { ActionItem } from "./types";
import { CardList } from "./CardList";
import { Box, Alert } from "@mui/material";

type Props = {
  entities: any[] | null;
  columns: {
    id: string;
    label?: string;
    value: string | Function;
    sort?: Function;
  }[];
  handleActionClick: (action: TABLE_ACTIONS, id: string) => void;
  fieldToFilter: string | ((data: any, filterName: string) => void);
  isSelectable: boolean;
  onSelectionChanged?: (selected: string[]) => void;
  entitiesIdsAlreadySelected?: string[];
  allowedMenuItems?: string[] | ((data: any) => string[]);
  extraActionItems: ActionItem[];
  idColumn: string;
  enableCardViewInSmallDevices?: boolean;
  enableMenuInSmallDevices?: boolean;
  displayActionInMenu?: boolean;
  sortDefault?: SortOrder;
  sortDefaultField?: string;
  renderCardContent?: (data: any) => React.ReactNode | undefined;
  filterOptions?: TableFilterOptions;
};

function CustomTable({
  entities,
  columns,
  handleActionClick,
  fieldToFilter,
  isSelectable,
  onSelectionChanged,
  entitiesIdsAlreadySelected,
  allowedMenuItems = [
    TABLE_ACTIONS.EDIT,
    TABLE_ACTIONS.DELETE,
    TABLE_ACTIONS.VIEW,
  ],
  extraActionItems,
  idColumn = "id",
  enableCardViewInSmallDevices = false,
  enableMenuInSmallDevices = true,
  displayActionInMenu = true,
  sortDefault = SortOrder.asc,
  sortDefaultField = "name",
  renderCardContent,
  filterOptions,
}: Readonly<Props>) {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState(sortDefault);
  const [selected, setSelected] = useState([] as string[]);
  const [orderBy, setOrderBy] = useState(sortDefaultField);
  const [filterName, setFilterName] = useState("");

  useEffect(() => {
    setSelected(entitiesIdsAlreadySelected || []);
  }, [entitiesIdsAlreadySelected]);

  const handleSort = (_: any, id: string) => {
    const isAsc = orderBy === id && order === SortOrder.asc;
    if (id !== "") {
      setOrder(isAsc ? SortOrder.desc : SortOrder.asc);
      setOrderBy(id);
    }
  };

  const handleClick = (event: any, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
    if (onSelectionChanged) {
      onSelectionChanged(newSelected);
    }
  };

  const getRowData = (row: any) => {
    const rowData = [];
    for (const r of columns) {
      if (r.id === "actions") {
        rowData.push("actions");
      } else if (typeof r.value === "function") {
        rowData.push(r.value(row));
      } else {
        rowData.push(row[r.id]);
      }
    }
    return rowData;
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event: any) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const getMenuItems = () => {
    return allowedMenuItems;
  };

  // Build comparator: prefer column's custom sort if provided
  const selectedColumn = columns.find((c) => c.id === orderBy);
  const comparator = (() => {
    const defaultComparator = getComparator(order, orderBy);
    if (!selectedColumn || typeof selectedColumn.sort !== "function") {
      return defaultComparator;
    }

    return getCustomComparator(order, selectedColumn.sort as any);
  })();

  const dataFiltered = applyFilter({
    inputData: entities || [],
    comparator,
    filterName,
    fieldToFilter,
  });

  const notFound = !dataFiltered.length && !!filterName;
  const isEmpty = !entities || entities.length === 0;
  const showToolbar = !isEmpty || Boolean(filterOptions?.onShowHiddenChange);

  return (
    <Box>
      {showToolbar && (
        <CustomTableToolbar
          numSelected={selected.length}
          filter={filterName}
          onFilter={handleFilterByName}
          filterOptions={filterOptions}
        />
      )}
      {isEmpty ? (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ textAlign: "center" }}>
            No data available to display
          </Alert>
        </Box>
      ) : (
        <>
          {enableCardViewInSmallDevices && (
            <Box sx={{ display: { xs: "block", md: "none" } }} mt={1}>
              <CardList
                data={dataFiltered.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage,
                )}
                columns={columns}
                idColumn={idColumn}
                handleActionClicked={(action, id) =>
                  handleActionClick(action, id)
                }
                allowedMenuItems={getMenuItems()}
                extraActionItems={extraActionItems}
                renderCardContent={renderCardContent}
                enableMenuInSmallDevices={enableMenuInSmallDevices}
              />
            </Box>
          )}

          <TableContainer
            sx={{
              overflowX: { xs: "auto", sm: "visible" },
              overflowY: "visible",
              display: {
                xs: enableCardViewInSmallDevices ? "none" : "table",
                md: "table",
              },
            }}
          >
            <Table>
              <CustomTableHead
                order={order}
                orderBy={orderBy}
                rowCount={entities?.length ?? 0}
                numSelected={selected.length}
                onRequestSort={handleSort}
                onSelectAllClick={() => {
                  if (selected.length === entities?.length) {
                    setSelected([]);
                  } else {
                    setSelected(entities?.map((row) => row[idColumn]) || []);
                  }
                }}
                headLabel={columns}
                isSelectable={isSelectable}
              />
              <TableBody>
                {dataFiltered
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: any) => (
                    <CustomTableRow
                      key={row[idColumn]}
                      data={getRowData(row)}
                      row={row}
                      selected={selected.indexOf(row[idColumn]) !== -1}
                      handleSelected={(event: any) =>
                        handleClick(event, row[idColumn])
                      }
                      handleActionClicked={(action: TABLE_ACTIONS) =>
                        handleActionClick(action, row[idColumn])
                      }
                      isSelectable={isSelectable}
                      allowedMenuItems={getMenuItems()}
                      extraActionItems={extraActionItems}
                      displayActionInMenu={displayActionInMenu}
                    />
                  ))}

                <TableEmptyRows
                  height={77}
                  emptyRows={emptyRows(
                    page,
                    rowsPerPage,
                    entities?.length ?? 0,
                  )}
                />

                {notFound && <TableNoData query={filterName} />}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            page={page}
            component="div"
            count={entities?.length ?? 0}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              overflow: "visible",
              "& .MuiTablePagination-toolbar": {
                flexWrap: "wrap",
                justifyContent: { xs: "center", sm: "flex-end" },
                rowGap: 0.5,
                px: { xs: 0, sm: 2 },
              },
              "& .MuiTablePagination-spacer": {
                display: { xs: "none", sm: "block" },
              },
            }}
          />
        </>
      )}
    </Box>
  );
}

export default memo(CustomTable);
